const suppliers = ['BNK', 'BEYOND BANK AUSTRALIA', 'MACQUARIE'] as const
type Supplier = typeof suppliers[number]

type GenericFeatures = {
  additionalSupplierDetail?: boolean
  showFundingSection?: boolean
  requiresDirectorContact?: boolean
}
type BnkFeatures = {}
type BeyondBankFeatures = {}
type MacquarieFeatures = {
  previousMacquarieIdentification?: boolean
  macquarieCreditInterestAccount?: boolean
}
type SupplierFeatures = GenericFeatures & BnkFeatures & BeyondBankFeatures & MacquarieFeatures

type GenericResources = {
  cancellationForm?: string
  cashSecurity?: {
    completedDescription?: string
    completedTitle?: string
    pendingDescription?: string
    pendingTitle?: string
  }
}
type BnkResources = {}
type BeyondBankResources = {}
type MacquarieResources = {}
type SupplierResources = GenericResources & BnkResources & BeyondBankResources & MacquarieResources

type SupplierConfig = {
  name: Supplier
  tradeName: string
  features: SupplierFeatures
  resources: SupplierResources
}

const supplierConfigs = new Map<Supplier, SupplierConfig>([
  [
    'BNK',
    {
      name: 'BNK',
      tradeName: 'BNK Banking Corporation Limited',
      features: { requiresDirectorContact: true },
      resources: { cancellationForm: '/BNKCancellationForm.pdf' }
    }
  ],
  [
    'BEYOND BANK AUSTRALIA',
    { name: 'BEYOND BANK AUSTRALIA', tradeName: 'Beyond Bank Australia Limited', features: {}, resources: {} }
  ],
  [
    'MACQUARIE',
    {
      name: 'MACQUARIE',
      tradeName: 'Macquarie Bank Private Limited',
      features: {
        previousMacquarieIdentification: true,
        macquarieCreditInterestAccount: true,
        additionalSupplierDetail: true,
        showFundingSection: true
      },
      resources: {
        cancellationForm: '/MacquarieCancellationForm.pdf',
        cashSecurity: {
          completedDescription:
            'The cash security is now direct debited by the bank using account details nominated in the application form.',
          completedTitle: 'Cash security direct debited by the bank',
          pendingDescription:
            'The cash security will be direct debited by the bank using account details nominated in the application form.',
          pendingTitle: 'Cash security to be direct debited by the bank'
        }
      }
    }
  ]
])

export const isValidSupplier = (supplier: string | undefined): supplier is Supplier => {
  return typeof supplier === 'string' && suppliers.includes(supplier?.toUpperCase() as Supplier)
}

/**
 * Check if a feature is enabled for a specific supplier
 */
export const featureEnabledFor = (supplier: string | undefined, feature: keyof SupplierFeatures): boolean | string => {
  if (!isValidSupplier(supplier)) return false

  const config = supplierConfigs.get(supplier.toUpperCase() as Supplier)

  if (!config) return false

  const features = config.features

  if (!(feature in features)) return false

  return !!features[feature as keyof typeof features]
}

/**
 * Get a resource for a specific supplier
 */
export const getResourceFor = <K extends keyof SupplierResources>(
  supplier: string | undefined,
  resource: K
): SupplierResources[K] => {
  if (!isValidSupplier(supplier)) {
    return undefined
  }

  const config = supplierConfigs.get(supplier.toUpperCase() as Supplier)

  if (!config) {
    return undefined
  }

  const resources = config.resources

  if (!(resource in resources)) {
    return undefined
  }

  return resources[resource]
}

export const getSupplierTradeName = (supplier: string | undefined): string => {
  return supplierConfigs.get(supplier as Supplier)?.tradeName ?? 'The Bank'
}
